/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import classnames from 'classnames';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {EmergencyIcon} from '@ion/dls/src/icons/EmergencyIcon';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import Address from '@ion/dls/src/maps/components/Address';
import {dlsColor, dlsLayout, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {EmergencyDialog} from '@/components/dialogs/EmergencyDialog';
import {showEmergencyDialog} from '@/store/actions/dashboard';
import {
  emergencyDialogSelector,
  selectedAssetSelector,
  settingsSelector
} from '@/store/selectors/dashboard-selectors';
import {ContactTags, getContacts} from '../../components/dialogs/EmergencyDialog';
import {AssetDetailLabelTypes} from '../../types/types';
import {resolveValue} from '../../utils/ComponentUtils';
import {getStatus, isWarningStatus} from './SubscriptionDetails';

interface AssetDetails {
  asset: CVSAssetType;
  labels: AssetDetailLabelTypes;
}

interface AssetDetailAddressProps {
  street: string;
  crossStreet: string;
  city: string;
  state: string;
  align: string;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  content : {
    paddingLeft   : dlsLayout('gutters', 'mobile'),
    paddingRight  : dlsLayout('gutters', 'mobile') + 10,
    paddingTop    : 0,
    paddingBottom : '10px'
  },
  loader : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center'
  },
  alignment : {
    display    : 'inline-flex',
    alignItems : 'center'
  },
  details : {
    ...dlsTypography('panelCards'),
    color     : dlsColor('grey2'),
    minHeight : 64
  },
  emergency : {
    fontSize    : '14px',
    color       : dlsColor('caBlue'),
    paddingLeft : '24px',
    paddingTop  : '24px'
  },
  error : {
    color : dlsColor('caRed')
  },
  align : {
    textAlign : 'right'
  },
  emergencyLink : {
    height : '40px'
  }
}));

const validateStreet = (street: string): string => {
  return (isNil(street) || street?.toLowerCase().includes('unnamed')) ? '' : street;
};

export const AssetDetailAddress = ({
  street,
  crossStreet,
  city,
  state,
  align
}: AssetDetailAddressProps) => {
  const street1 = validateStreet(street);
  const crossStreet1 = validateStreet(crossStreet);
  const formattedStreet = `${street1} ${!isEmpty(crossStreet1) ? capitalizeFirstLetter(translate('common:near')) : ''} ${crossStreet1}`;

  return (
    <Address textAlign={align}
      city={resolveValue(city)}
      state={resolveValue(state)}
      street={formattedStreet} />
  );
};

/**
 * The content of the list items in the asset card differ in content but
 * share styles depending on what's being displayed. So we can reuse it with
 * this component.
 *
 * ```typescript
 *
 *   import React from 'react';
 *   import AssetDetails from '@ion/dls/src/components/cards/AssetCardListContent';
 *   import { theme } from '@ion/dls/src/theme/theme';
 *
 *   <AssetDetails asset={asset}
 *     labels={labels} />
 *
 * ```
 *
 */
const AssetDetails = ({asset, labels}: AssetDetails) => {
  const classes                = useStyles();
  const dispatch               = useDispatch();
  const emergencyDialogShow    = useSelector(emergencyDialogSelector);
  const selectedAsset          = useSelector(selectedAssetSelector);
  const settings               = useSelector(settingsSelector);
  const contactDetails         = getContacts(settings);
  const {
    name,
    vin,
    street,
    crossStreet,
    city,
    state
  } = asset;
  const {currentPlan}  = selectedAsset?.subscriptionData || {};

  function launchEmergencyDialog() {
    dispatch(showEmergencyDialog(true));
  };

  return (
    <>
      <Accordion children=''
        defaultExpanded={true}
        header={
          <div className={classes.alignment}>
            <div className={classes.details}>
              {translate('sidesheet:assetCard.header')}
            </div>
          </div>
        }
        details={
          <><div className={classes.content}>
            <CardItemRow label={labels.name}
              isLabelColon={false}
              isCenterAligned={false}
              breakWord={{wordBreak: 'break-all'}}
              value={name} />
            {getStatus(currentPlan) !=='stolen' && <CardItemRow label={labels.location}
              isLabelColon={false}
              isCenterAligned={false}
              value={!(isWarningStatus(getStatus(currentPlan)))
                ? (<AssetDetailAddress align={'right'}
                  street={street}
                  crossStreet={crossStreet}
                  city={city}
                  state={state} />)
                : translate('sidesheet:assetDetails.unknown')} />}
            <CardItemRow label={labels.vin}
              isLabelColon={false}
              isCenterAligned={false}
              value={resolveValue(vin)} />
            {getStatus(currentPlan) === 'stolen' && (
              <>
                <CardItemRow label={labels.status}
                  isLabelColon={false}
                  isCenterAligned={false}
                  value={<div className={classes.error}>
                    {translate('sidesheet:assetDetails.theftReport')}
                  </div>} />
                <CardItemRow label={labels.note}
                  isLabelColon={false}
                  isCenterAligned={false}
                  value={<div className={classnames(classes.align, classes.error)}>
                    <div>{translate('sidesheet:assetDetails.trackerDetails')}</div>
                    <div>{contactDetails[ContactTags.theftAssist]}</div>
                  </div>} />
              </>)}
          </div><Divider /><div className={classes.emergencyLink}>
            <TertiaryButton className={classes.emergency} startIcon={<EmergencyIcon color={dlsColor('caBlue')}/>}
              onClick={launchEmergencyDialog}>
              <span>{translate('sidesheet:assetCard.emergency')}</span>
            </TertiaryButton>
          </div></>
        } />
      {emergencyDialogShow && <EmergencyDialog />}
    </>
  );
};

export default AssetDetails;
